import React, { createContext, useContext, useRef, ReactNode, useCallback, useMemo } from 'react'
import { Toast } from 'primereact/toast'

type ToastSeverity = 'success' | 'info' | 'warn' | 'error'

interface ToastContextProps {
    notify: (severity: ToastSeverity, summary: ReactNode, detail?: ReactNode, life?: number) => void
    notifySuccess: (summary: ReactNode, detail?: ReactNode, life?: number) => void
    notifyError: (summary: ReactNode, detail?: ReactNode, life?: number) => void
    notifyInfo: (summary: ReactNode, detail?: ReactNode, life?: number) => void
    notifyWarn: (summary: ReactNode, detail?: ReactNode, life?: number) => void
}

const ToastContext = createContext<ToastContextProps | null>(null)

interface ToastProviderProps {
    children: ReactNode
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
    const toastRef = useRef<Toast>(null)

    const notify = useCallback((severity: ToastSeverity, summary: ReactNode, detail?: ReactNode, life = 3000) => {
        toastRef.current?.show({ severity, summary, detail, life })
    }, [])

    const notifySuccess = useCallback(
        (summary: ReactNode, detail?: ReactNode, life?: number) => {
            notify('success', summary, detail, life)
        },
        [notify]
    )

    const notifyError = useCallback(
        (summary: ReactNode, detail?: ReactNode, life?: number) => {
            notify('error', summary, detail, life)
        },
        [notify]
    )

    const notifyInfo = useCallback(
        (summary: ReactNode, detail?: ReactNode, life?: number) => {
            notify('info', summary, detail, life)
        },
        [notify]
    )

    const notifyWarn = useCallback(
        (summary: ReactNode, detail?: ReactNode, life?: number) => {
            notify('warn', summary, detail, life)
        },
        [notify]
    )

    const contextValue = useMemo<ToastContextProps>(
        () => ({
            notify,
            notifySuccess,
            notifyError,
            notifyInfo,
            notifyWarn,
        }),
        [notify, notifySuccess, notifyError, notifyInfo, notifyWarn]
    )

    return (
        <ToastContext.Provider value={contextValue}>
            <Toast ref={toastRef} />
            {children}
        </ToastContext.Provider>
    )
}

export const useToast = (): ToastContextProps => {
    const context = useContext(ToastContext)
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider')
    }
    return context
}
